import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { StyleSheet, Image, Animated, Pressable } from 'react-native';
import { AnimatedDNABox, DNABox, DNAButton, DNAText, Iffy, util } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import useMachineSelector, { composite } from 'src/hooks/useSelector';
import * as navigationMenuSelector from 'src/state/machines/navigationMenu/navigationMenu.selector';
import NavigationMenuStateProvider, {
  useNavigationMenuState,
} from 'src/state/machines/navigationMenu/NavigationMenuProvider';
import { useAppSettings } from 'src/state/context/AppSettings';
import TenantLogo from 'src/components/DNA/Header/TenantLogo';
import NavigationItem from './DNANavigationItem';
import useCurrentPage from 'src/components/DNA/hooks/useCurrentPage';
import ROUTES from 'src/router/routeDef';

const CONTAINER_COLLAPSED_WIDTH = 78;
const CONTAINER_COLLAPSED_WIDTH_TABLET = 84;
const CONTAINER_EXPANDED_WIDTH = 234;

const S = StyleSheet.create({
  logoContainer: {
    height: 72
  },
  logoExpandedModifier: {
    paddingLeft: 30,
  },
  beaconLogo: {
    marginLeft: 32,
    height: 28,
    width: 32,
  },
  tenantLogo: {
    width: 174,
    height: 57,
    borderRadius: 6,
    borderStyle: 'solid',
    overflow: 'hidden',
  },
  publisherViewIndicatorContainer: {
    backgroundColor: colors['color-white-transparent-16'],
    height: 20,
    marginBottom: 16,
  },
  publisherViewIndicatorText: {
    color: colors['color-white'],
    fontWeight: '600',
    fontSize: 11,
  },
  navigationMenuContainer: {
    backgroundColor: colors['color-ui2-brand-900'],
  },
  toggleCollapseButton: {
    position: 'absolute',
    backgroundColor: colors['color-ui2-brand-900'],
    borderColor: colors['color-ui2-brand-900'],
    zIndex: 1000,
    right: -12,
    bottom: 56,
  },
  toggleCollapseButtonTablet: {
    position: 'absolute',
    backgroundColor: colors['color-ui2-brand-900'],
    borderColor: colors['color-ui2-brand-900'],
    zIndex: 1000,
    right: -22,
    bottom: 46,
  },
  hoveredToggleCollapseButton: {
    backgroundColor: colors['color-white-transparent-16'],
  },
})

const LogoContainer: React.FC = () => {
  const { service, navigateTo } = useNavigationMenuState();

  const cond = useMachineSelector(
    service,
    (state) => composite(
      state,
      navigationMenuSelector.isMenuCollapsed,
    ),
  )

  const navHome = useCallback(
    () => navigateTo(ROUTES.HOME),
    [navigateTo],
  )

  const logoStyle = useMemo(
    () => ({
      ...S.logoContainer,
      ...(cond.isMenuCollapsed ? undefined : S.logoExpandedModifier)
    }),
    [cond.isMenuCollapsed]
  )

  return (
    <DNABox alignY="center" style={logoStyle}>
      <Pressable onPress={navHome}>
        <Iffy is={cond.isMenuCollapsed}>
          <Image
            style={S.beaconLogo}
            source={require('../../../../assets/images/BeaconLogoWhiteAlt.svg')}
          />
        </Iffy>
        <Iffy is={!cond.isMenuCollapsed}>
          <TenantLogo size="medium" style={S.tenantLogo} />
        </Iffy>
      </Pressable>
    </DNABox>
  )
}

const PublisherViewIndicator: React.FC = () => {
  const { service } = useNavigationMenuState();

  const cond = useMachineSelector(
    service,
    (state) => composite(
      state,
      navigationMenuSelector.isInPublisherView,
      navigationMenuSelector.isMenuCollapsed,
    ),
  )

  if (!cond.isInPublisherView) return null;
  return (
    <DNABox appearance="row" alignX="center" alignY="center" style={S.publisherViewIndicatorContainer}>
      <DNAText style={S.publisherViewIndicatorText}>
        {cond.isMenuCollapsed ? 'PUB' : 'PUBLISHER'}
      </DNAText>
    </DNABox>
  )
}

const DNANavigationMenu: React.FC = () => {
  const { service, toggleCollapseState } = useNavigationMenuState();
  const { deviceMode } = useAppSettings();
  const isTablet = deviceMode === 'tablet';

  const cond = useMachineSelector(
    service,
    (state) => composite(
      state,
      navigationMenuSelector.isMenuCollapsed,
      navigationMenuSelector.displayingNavigationItems,
    ),
  )

  const animatedWidth = useRef(new Animated.Value(
    cond.isMenuCollapsed
      ? (isTablet ? CONTAINER_COLLAPSED_WIDTH_TABLET : CONTAINER_COLLAPSED_WIDTH)
      : CONTAINER_EXPANDED_WIDTH,
  )).current;

  useEffect(() => {
    const animation = Animated.timing(animatedWidth, {
      toValue: cond.isMenuCollapsed
        ? (isTablet ? CONTAINER_COLLAPSED_WIDTH_TABLET : CONTAINER_COLLAPSED_WIDTH)
        : CONTAINER_EXPANDED_WIDTH,
      duration: 250,
      useNativeDriver: false,
    });
    animation.start();
    return () => {
      animation.stop();
    };
  }, [cond.isMenuCollapsed, isTablet, animatedWidth]);

  // Compose animated style
  const containerStyle = useMemo(() => util.mergeStyles(
    undefined,
    S.navigationMenuContainer,
    { width: animatedWidth },
  ), [animatedWidth]);

  return (
    // TODO: this test id is used for the useFixedLayout hook, this is more a hack than a final solution
    // we need to reestructure the containers that used this hook to use a different approach also we need to modify
    // the app container to use a different approach, for now please do not remove this test id
    <AnimatedDNABox appearance="col" style={containerStyle} testID="navigation-menu">
      {/* NAVIGATION MENU CONTENT */}
      <DNABox fill appearance="col">
        {/* LOGO */}
        <LogoContainer />
        {/* PUBLISHER VIEW INDICATOR */}
        <PublisherViewIndicator />
        {/* NAVIGATION ITEMS */}
        <DNABox fill appearance="col">
          {cond.displayingNavigationItems.map((navigationItem) => (
            <NavigationItem key={navigationItem.route.PATH} navigationItem={navigationItem} depthIndex={0} />
          ))}
        </DNABox>
      </DNABox>
      {/* TOGGLE COLLAPSE BUTTON */}
      <DNAButton
        size={isTablet ? 'xl' : 'sm'}
        iconLeft={cond.isMenuCollapsed ? 'caret-double-right' : 'caret-double-left'}
        onPress={toggleCollapseState}
        style={isTablet ? S.toggleCollapseButtonTablet : S.toggleCollapseButton}
      />
    </AnimatedDNABox>
  );
}

const DNANavigationMenuWrapper: React.FC = () => {
  const currentPage = useCurrentPage();
  // Check if navigation menu should be disabled for admin users
  const isDisabledOnCurrentRoute = currentPage?.configOptions?.desktop?.disableGlobalNavigationMenu;

  // Hide navigation menu if disabled on current route or if user is admin
  if (isDisabledOnCurrentRoute) {
    return null;
  }

  return (
    <NavigationMenuStateProvider>
      <DNANavigationMenu />
    </NavigationMenuStateProvider>
  )
}

export default DNANavigationMenuWrapper
